import React, { useState, useEffect, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CloseIcon from '@mui/icons-material/Close';
import { SearchOption } from './SearchOption';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonWithPopup } from '../../../common/ButtonWithPopup';
import { PopupContent, PopupSectionTitle, PopupEmpty } from '../../../common/Popup';
import { setSearchInput } from '../../../../store/Actions';

export function Search() {
  const filteredEvents = useSelector(state=>state.filteredEvents);
  const eventList = useMemo(() => filteredEvents ? Object.values(filteredEvents) : [], [filteredEvents]);
  const [eventResults, setEventResults] = useState([]);
  const [inputRef, setInputRef] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);
  const appWidth = useSelector(state => state.appWidth);
  const inputValue = useSelector(state => state.searchInput);
  const dispatch = useDispatch();
  const isMobile = appWidth <= 560;

  // Wait for the input element to mount and then focus it after an explicit
  // "focus the input next" request (e.g. tapping the mobile search icon).
  useEffect(() => {
    if (shouldFocusInput && inputRef) {
      inputRef.focus();
      setShouldFocusInput(false);
    }
  }, [shouldFocusInput, inputRef]);

  useEffect(() => {
    if (isMobile && popupOpen && inputRef) {
      inputRef.focus();
    }
  }, [isMobile, popupOpen, inputRef]);

  // Build the dropdown's event list. With no query, surface the first 5
  // filtered events as a quick-pick. With a query, show up to 5 filtered
  // events whose title or location matches.
  useEffect(() => {
    const normalizedInput = inputValue.trim().toLowerCase();

    const delayDebounceFn = setTimeout(() => {
      if (!normalizedInput) {
        setEventResults(eventList.slice(0, 5));
        return;
      }
      setEventResults(eventList.filter((event) => {
        if(event.title.toLowerCase().includes(normalizedInput))
          return true;
        if(event.location?.description?.toLowerCase().includes(normalizedInput))
          return true;
        return false;
      }).slice(0, 5));
    }, 0);
    return () => clearTimeout(delayDebounceFn);
  }, [inputValue, eventList]);


  const hasValue = !!inputValue;
  const expanded = focused || hasValue;
  const mobileButtonComponent = (
    <SearchIcon style={{ fontSize: 19, color: '#525252', flexShrink: 0 }} />
  );

  const desktopButtonComponent = (
    <div
      className={`header-search ${expanded ? 'is-expanded' : ''}`}
      style={{
        display:'flex',
        flexDirection:'row',
        alignItems:'center',
        gap:'10px',
        width: expanded ? '460px' : '360px',
        transition: 'width 220ms cubic-bezier(0.2, 0.8, 0.4, 1)',
      }}
    >
      <SearchIcon style={{ fontSize: 20, color: '#525252', flexShrink: 0 }} />
      <input
        type='text'
        value={inputValue}
        onChange={(event) => dispatch(setSearchInput(event.target.value))}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        ref={(ref) => { setInputRef(ref) }}
        placeholder='Search events'
        className='searchInput'
        style={{ flex:1, fontSize:'13.5px' }}
      />
      {hasValue && (
        <CloseIcon
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            dispatch(setSearchInput(''));
            if (inputRef) inputRef.focus();
          }}
          style={{ fontSize: 18, color: '#737373', cursor: 'pointer', flexShrink: 0 }}
        />
      )}
    </div>
  );

  const buttonComponent = isMobile ? mobileButtonComponent : desktopButtonComponent;

  const mobileSearchField = (
    <div
      style={{
        display:'flex',
        flexDirection:'row',
        alignItems:'center',
        gap:'10px',
        margin:'10px 12px 8px',
        padding:'10px 12px',
        border:'1px solid rgba(0,0,0,0.14)',
        borderRadius:'10px',
        background:'#ffffff',
      }}
    >
      <SearchIcon style={{ fontSize: 20, color: '#525252', flexShrink: 0 }} />
      <input
        type='text'
        value={inputValue}
        onChange={(event) => dispatch(setSearchInput(event.target.value))}
        ref={(ref) => { setInputRef(ref) }}
        placeholder='Search events'
        className='searchInput'
        style={{ flex:1, fontSize:'16px', minWidth:0 }}
      />
      {hasValue && (
        <CloseIcon
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
            dispatch(setSearchInput(''));
            if (inputRef) inputRef.focus();
          }}
          style={{ fontSize: 18, color: '#737373', cursor: 'pointer', flexShrink: 0 }}
        />
      )}
    </div>
  );

  const popupWidth = isMobile ? '100%' : 540;
  const iconButtonStyle = isMobile ? {
    width: '40px',
    height: '40px',
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'center',
  } : {};

  const popupComponent = (
    <PopupContent style={{ width: popupWidth }}>
      {isMobile && mobileSearchField}
      <PopupSectionTitle>Your events</PopupSectionTitle>
      {eventResults.length > 0 ? (
        eventResults.map((event) =>
          <SearchOption key={event.id} event={event} setPopupOpen={setPopupOpen}/>
        )
      ) : (
        <PopupEmpty><SearchOffIcon/>No matching events</PopupEmpty>
      )}
    </PopupContent>
  );

  const onButtonClick = () => {
    if (isMobile) {
      setShouldFocusInput(true);
    } else if (inputRef) {
      inputRef.focus();
    }
  }

  return (
    <ButtonWithPopup
      buttonComponent={buttonComponent}
      popupComponent={popupComponent}
      onClick={onButtonClick}
      popupOpen={popupOpen}
      setPopupOpen={setPopupOpen}
      buttonStyle={iconButtonStyle}
      popupClassName={isMobile ? 'mobile-header-popup' : ''}
      toggleOnClick
    />
  );
}
